/*
  Phone swipe gestures (Ami Markt).

  - Swipe a drawer back the way it came to close it: cart drawer, menu, filters.
  - Swipe a pop-up down to close it (search and other dialog-modal pop-ups),
    only when its content is scrolled to the top.
  - Full-screen product photo: swipe down to close, swipe left/right for the
    next/previous photo, once the photo itself can't pan any further that way.
  - Swipe a cart item left to remove it, with an Undo that puts it back.

  Phones only (below 750px). Nothing here replaces a button: every action
  still has its X, Back or delete button.
*/
import { CartAddEvent } from '@theme/events';

const PHONE = window.matchMedia('(max-width: 749px)');
const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
const DECIDE_PX = 10; // movement before a gesture picks a direction
const COMMIT_SHARE = 0.3; // share of the panel's size that commits the gesture
const FLICK_PX_PER_MS = 0.5; // a quick flick commits even when short
const ROW_COMMIT_SHARE = 0.4;
const UNDO_MS = 6000;

const config = document.getElementById('otc-gestures')?.dataset ?? {};
const text = {
  removed: config.removed || 'Removed from cart',
  undo: config.undo || 'Undo',
  remove: config.remove || 'Remove',
  undoFailed: config.undoFailed || 'Could not put it back. Please add it again.',
};

/** @type {null | Gesture} */
let gesture = null;

/**
 * @typedef {object} Gesture
 * @property {number} x0
 * @property {number} y0
 * @property {number} t0
 * @property {Element} target
 * @property {null | Target} active
 * @property {number} lastX
 * @property {number} lastY
 * @property {number} lastT
 * @property {number} vx
 * @property {number} vy
 * @property {null | HTMLElement} photo - the zoomable photo the touch started on
 * @property {number} photoX - where the photo sat when the touch started
 * @property {number} photoY
 */

/**
 * @typedef {object} Target
 * @property {'left' | 'right' | 'down' | 'row' | 'zoom-next'} kind
 * @property {HTMLElement} el - the element that follows the finger
 * @property {() => void} commit
 */

document.addEventListener('touchstart', onStart, { passive: true, capture: true });
document.addEventListener('touchmove', onMove, { passive: false, capture: true });
document.addEventListener('touchend', onEnd, { passive: true, capture: true });
document.addEventListener('touchcancel', () => cancel(), { passive: true, capture: true });

/** @param {TouchEvent} event */
function onStart(event) {
  if (!PHONE.matches || event.touches.length !== 1 || !(event.target instanceof Element)) {
    cancel();
    return;
  }
  if (gesture?.active) cancel();
  const touch = event.touches[0];
  const now = event.timeStamp;
  gesture = {
    x0: touch.clientX,
    y0: touch.clientY,
    t0: now,
    target: event.target,
    active: null,
    lastX: touch.clientX,
    lastY: touch.clientY,
    lastT: now,
    vx: 0,
    vy: 0,
    photo: null,
    photoX: 0,
    photoY: 0,
  };
  const photo = event.target.closest('zoom-dialog drag-zoom-wrapper');
  if (photo instanceof HTMLElement) {
    gesture.photo = photo;
    [gesture.photoX, gesture.photoY] = photoOffset(photo);
  }
}

/**
 * How far the theme has panned a full-screen photo.
 * @param {HTMLElement} photo
 * @returns {[number, number]}
 */
function photoOffset(photo) {
  return [
    parseFloat(photo.style.getPropertyValue('--drag-zoom-translate-x')) || 0,
    parseFloat(photo.style.getPropertyValue('--drag-zoom-translate-y')) || 0,
  ];
}

/** @param {TouchEvent} event */
function onMove(event) {
  if (!gesture) return;
  if (event.touches.length !== 1) {
    cancel();
    return;
  }
  const touch = event.touches[0];
  const dt = Math.max(1, event.timeStamp - gesture.lastT);
  gesture.vx = (touch.clientX - gesture.lastX) / dt;
  gesture.vy = (touch.clientY - gesture.lastY) / dt;
  gesture.lastX = touch.clientX;
  gesture.lastY = touch.clientY;
  gesture.lastT = event.timeStamp;

  const dx = touch.clientX - gesture.x0;
  const dy = touch.clientY - gesture.y0;

  if (!gesture.active) {
    // A full-screen photo opens zoomed in and pans with one finger. Wait a little longer
    // so the theme has moved it, then only take the swipe if the photo couldn't move
    // that way (it is at its edge).
    if (Math.hypot(dx, dy) < (gesture.photo ? DECIDE_PX * 2 : DECIDE_PX)) return;
    const target = pickTarget(gesture, dx, dy);
    if (!target) {
      gesture = null;
      return;
    }
    gesture.active = target;
    target.el.style.transition = 'none';
    target.el.style.willChange = 'translate';
  }

  if (event.cancelable) event.preventDefault();
  follow(gesture.active, dx, dy);
}

function onEnd() {
  if (!gesture?.active) {
    gesture = null;
    return;
  }
  const { active } = gesture;
  const dx = gesture.lastX - gesture.x0;
  const dy = gesture.lastY - gesture.y0;
  const { vx, vy } = gesture;
  gesture = null;

  const rect = active.el.getBoundingClientRect();
  let commit = false;
  switch (active.kind) {
    case 'right':
      commit = dx > rect.width * COMMIT_SHARE || (dx > 30 && vx > FLICK_PX_PER_MS);
      break;
    case 'left':
      commit = -dx > rect.width * COMMIT_SHARE || (dx < -30 && vx < -FLICK_PX_PER_MS);
      break;
    case 'down':
      commit = dy > Math.min(rect.height, window.innerHeight) * 0.2 || (dy > 30 && vy > FLICK_PX_PER_MS);
      break;
    case 'row':
      commit = -dx > rect.width * ROW_COMMIT_SHARE || (dx < -40 && vx < -FLICK_PX_PER_MS);
      break;
    case 'zoom-next':
      commit = Math.abs(dx) > 60 || (Math.abs(dx) > 30 && Math.abs(vx) > FLICK_PX_PER_MS);
      break;
  }

  if (commit) {
    active.commit();
  } else {
    snapBack(active.el);
  }
}

function cancel() {
  if (gesture?.active) snapBack(gesture.active.el);
  gesture = null;
}

/**
 * Moves the element with the finger. `translate` is used rather than `transform`,
 * so it adds to the theme's own open/close animations instead of fighting them.
 * @param {Target} target
 * @param {number} dx
 * @param {number} dy
 */
function follow(target, dx, dy) {
  const { el, kind } = target;
  switch (kind) {
    case 'right':
      el.style.translate = `${Math.max(0, dx)}px 0`;
      break;
    case 'left':
      el.style.translate = `${Math.min(0, dx)}px 0`;
      break;
    case 'down':
      el.style.translate = `0 ${Math.max(0, dy)}px`;
      break;
    case 'row':
      el.style.translate = `${Math.min(0, dx)}px 0`;
      el.classList.add('otc-swipe-row--active');
      break;
    case 'zoom-next':
      el.style.translate = `${dx * 0.4}px 0`;
      break;
  }
}

/** @param {HTMLElement} el */
function snapBack(el) {
  el.style.transition = REDUCED_MOTION.matches ? 'none' : 'translate 200ms ease';
  el.style.translate = '';
  const done = () => {
    el.style.transition = '';
    el.style.willChange = '';
    el.classList.remove('otc-swipe-row--active');
  };
  if (REDUCED_MOTION.matches) done();
  else setTimeout(done, 220);
}

/**
 * Slides the element the rest of the way out, hides it, then runs the theme's own close.
 * @param {HTMLElement} el
 * @param {string} to - final translate value
 * @param {() => unknown} close
 * @param {() => void} [whenClosed] - resets the element once the theme has closed it
 */
function slideOutThen(el, to, close, whenClosed) {
  const finish = () => {
    // Hidden rather than faded: the theme's closing animation still runs (and resolves),
    // but out of sight, so the panel doesn't jump back before it leaves.
    el.style.visibility = 'hidden';
    Promise.resolve(close()).finally(() => {
      setTimeout(() => {
        el.style.translate = '';
        el.style.transition = '';
        el.style.visibility = '';
        el.style.willChange = '';
        whenClosed?.();
      }, 50);
    });
  };
  if (REDUCED_MOTION.matches) {
    finish();
    return;
  }
  el.style.transition = 'translate 180ms ease-out';
  el.style.translate = to;
  setTimeout(finish, 190);
}

/**
 * Works out which swipe (if any) the touch belongs to, from where it started
 * and which way it is heading.
 * @param {Gesture} g
 * @param {number} dx
 * @param {number} dy
 * @returns {Target | null}
 */
function pickTarget(g, dx, dy) {
  const start = g.target;
  const horizontal = Math.abs(dx) > Math.abs(dy) * 1.2;
  const vertical = Math.abs(dy) > Math.abs(dx) * 1.2;
  if (!horizontal && !vertical) return null;
  if (start.closest('input[type="range"], textarea, [contenteditable="true"], model-viewer, video, iframe')) return null;

  // Full-screen product photo.
  const zoom = start.closest('zoom-dialog');
  if (zoom) {
    if (g.photo) {
      const [x, y] = photoOffset(g.photo);
      if (horizontal ? Math.abs(x - g.photoX) > 2 : Math.abs(y - g.photoY) > 2) return null;
    }
    return zoomTarget(zoom, start, dx, dy, horizontal);
  }

  // Cart item: swipe left to remove.
  const row = start.closest('cart-items-component .cart-items__table-row[data-key]');
  if (row instanceof HTMLElement && horizontal && dx < 0 && !scrollsSideways(start, row)) {
    const removeButton = row.querySelector('.cart-items__remove');
    if (removeButton instanceof HTMLButtonElement && !removeButton.hidden && !removeButton.disabled) {
      return rowTarget(row, removeButton);
    }
  }

  // Menu drawer: swipe left to close.
  const headerDrawer = start.closest('header-drawer');
  if (headerDrawer) {
    const details = headerDrawer.querySelector('details.menu-drawer-container');
    const panel = headerDrawer.querySelector('.menu-drawer');
    if (!(details instanceof HTMLDetailsElement) || !details.open || !(panel instanceof HTMLElement)) return null;
    if (!panel.contains(start) || !horizontal || dx > 0 || scrollsSideways(start, panel)) return null;
    return {
      kind: 'left',
      el: panel,
      commit: () => slideOutThen(panel, '-100% 0', () => /** @type {any} */ (headerDrawer).close?.()),
    };
  }

  // Dialogs: drawers slide back to their side, pop-ups slide down.
  const dialog = start.closest('dialog[open]');
  if (!(dialog instanceof HTMLDialogElement)) return null;
  const component = dialog.closest('dialog-component, cart-drawer-component');
  const close = () => {
    const c = /** @type {any} */ (component);
    if (c?.closeDialog) return c.closeDialog();
    dialog.close();
  };

  if (dialog.classList.contains('dialog-drawer')) {
    const side = drawerSide(dialog);
    if (!horizontal || scrollsSideways(start, dialog)) return null;
    if (side === 'right' && dx > 0) {
      return { kind: 'right', el: dialog, commit: () => slideOutThen(dialog, '100% 0', close) };
    }
    if (side === 'left' && dx < 0) {
      return { kind: 'left', el: dialog, commit: () => slideOutThen(dialog, '-100% 0', close) };
    }
    return null;
  }

  if (dialog.classList.contains('dialog-modal')) {
    if (!vertical || dy < 0 || scrolledDown(start, dialog)) return null;
    return { kind: 'down', el: dialog, commit: () => slideOutThen(dialog, '0 100vh', close) };
  }

  return null;
}

/**
 * Which edge a drawer is attached to. Read from where it sits, since the theme's
 * class names don't say it plainly (`dialog-drawer--right` opens from the left).
 * @param {HTMLElement} dialog
 * @returns {'left' | 'right'}
 */
function drawerSide(dialog) {
  const rect = dialog.getBoundingClientRect();
  const atLeft = rect.left <= 1;
  const atRight = rect.right >= window.innerWidth - 1;
  if (atLeft && !atRight) return 'left';
  if (atRight && !atLeft) return 'right';
  return dialog.classList.contains('dialog-drawer--right') ? 'left' : 'right';
}

/**
 * @param {Element} zoom
 * @param {Element} start
 * @param {number} dx
 * @param {number} dy
 * @param {boolean} horizontal
 * @returns {Target | null}
 */
function zoomTarget(zoom, start, dx, dy, horizontal) {
  const dialog = zoom.querySelector('dialog');
  const gallery = zoom.querySelector('.dialog-zoomed-gallery');
  if (!(dialog instanceof HTMLDialogElement) || !dialog.open || !(gallery instanceof HTMLElement)) return null;
  if (start.closest('.dialog-thumbnails-list-container, .dialog-zoomed-gallery__close-button')) return null;


  const z = /** @type {any} */ (zoom);
  if (horizontal) {
    const thumbs = Array.from(zoom.querySelectorAll('.dialog-thumbnails-list__thumbnail'));
    if (thumbs.length < 2) return null;
    const current = Math.max(
      0,
      thumbs.findIndex((t) => t.getAttribute('aria-selected') === 'true')
    );
    const next = current + (dx < 0 ? 1 : -1);
    if (next < 0 || next >= thumbs.length) return null;
    return {
      kind: 'zoom-next',
      el: gallery,
      commit: () => {
        snapBack(gallery);
        z.selectThumbnail?.(next, { behavior: REDUCED_MOTION.matches ? 'instant' : 'smooth' });
      },
    };
  }

  if (dy < 0) return null;
  return {
    kind: 'down',
    el: gallery,
    commit: () => {
      // The theme's own close flies the photo back into the page, so just hand over to it.
      gallery.style.transition = '';
      gallery.style.translate = '';
      gallery.style.willChange = '';
      z.close?.();
    },
  };
}

/**
 * @param {HTMLElement} row
 * @param {HTMLButtonElement} removeButton
 * @returns {Target}
 */
function rowTarget(row, removeButton) {
  row.style.setProperty('--otc-swipe-label', JSON.stringify(text.remove));
  // Look the line up now, while the finger is still down, so Undo has what it needs.
  const lineInfo = fetchLine(row.dataset.key || '');
  return {
    kind: 'row',
    el: row,
    commit: () => {
      const title = row.querySelector('.cart-items__title')?.textContent?.trim() || '';
      row.style.transition = REDUCED_MOTION.matches ? 'none' : 'translate 160ms ease-out';
      row.style.translate = '-100% 0';
      setTimeout(
        () => {
          const host = row.closest('dialog[open]');
          removeButton.click();
          lineInfo.then((line) => showUndo(title, line, host instanceof HTMLDialogElement ? host : null));
        },
        REDUCED_MOTION.matches ? 0 : 170
      );
    },
  };
}

/**
 * @param {string} key
 * @returns {Promise<null | { id: number, quantity: number, properties?: object, selling_plan?: number }>}
 */
async function fetchLine(key) {
  try {
    const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    const cart = await response.json();
    const item = cart.items?.find((/** @type {any} */ i) => i.key === key);
    if (!item) return null;
    /** @type {{ id: number, quantity: number, properties?: object, selling_plan?: number }} */
    const line = { id: item.variant_id, quantity: item.quantity };
    if (item.properties && Object.keys(item.properties).length) line.properties = item.properties;
    if (item.selling_plan_allocation?.selling_plan?.id) line.selling_plan = item.selling_plan_allocation.selling_plan.id;
    return line;
  } catch {
    return null;
  }
}

/** @type {HTMLElement | null} */
let toast = null;
/** @type {ReturnType<typeof setTimeout> | undefined} */
let toastTimer;

/**
 * Shows "Removed … Undo" at the bottom of the screen, on top of everything.
 * While the cart drawer is open everything outside it can't be tapped, so the
 * message lives inside the drawer and moves out to the page if the drawer closes.
 * @param {string} title
 * @param {null | { id: number, quantity: number }} line
 * @param {null | HTMLDialogElement} host - the open drawer the item was in
 */
function showUndo(title, line, host) {
  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'otc-undo-toast';
    toast.setAttribute('popover', 'manual');
    toast.setAttribute('role', 'status');
    toast.innerHTML = '<span class="otc-undo-toast__text"></span><button type="button" class="otc-undo-toast__button"></button>';
    document.body.append(toast);
  }
  const label = toast.querySelector('.otc-undo-toast__text');
  const button = toast.querySelector('.otc-undo-toast__button');
  if (!(label instanceof HTMLElement) || !(button instanceof HTMLButtonElement)) return;

  label.textContent = title ? `${text.removed}: ${title}` : text.removed;
  button.textContent = text.undo;
  button.hidden = !line;
  button.disabled = false;
  button.onclick = async () => {
    if (!line) return;
    button.disabled = true;
    const ok = await putBack(line);
    if (ok) {
      hideUndo();
    } else {
      label.textContent = text.undoFailed;
      button.hidden = true;
      restartTimer();
    }
  };

  placeToast(host ?? document.body);
  if (host) {
    host.addEventListener(
      'close',
      () => {
        if (toast?.parentElement === host && toast.matches(':popover-open')) placeToast(document.body);
      },
      { once: true }
    );
  }
  restartTimer();
}

/**
 * (Re)shows the message as the last thing in the top layer, inside `parent`.
 * @param {HTMLElement} parent
 */
function placeToast(parent) {
  if (!toast) return;
  if (toast.matches(':popover-open')) toast.hidePopover();
  parent.append(toast);
  toast.showPopover();
}

function restartTimer() {
  clearTimeout(toastTimer);
  toastTimer = setTimeout(hideUndo, UNDO_MS);
}

function hideUndo() {
  clearTimeout(toastTimer);
  if (toast?.matches(':popover-open')) toast.hidePopover();
}

/**
 * Adds the removed line back and tells the theme, so the cart drawer, cart page
 * and cart count all redraw.
 * @param {{ id: number, quantity: number }} line
 */
async function putBack(line) {
  const sectionIds = new Set();
  document.querySelectorAll('cart-items-component').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.add(el.dataset.sectionId);
  });
  try {
    const response = await fetch(window.Theme?.routes?.cart_add_url ?? '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: [line],
        sections: Array.from(sectionIds).join(','),
        sections_url: window.location.pathname,
      }),
    });
    const added = await response.json();
    if (!response.ok || added.status) return false;

    const cartResponse = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, {
      headers: { Accept: 'application/json' },
    });
    const cart = await cartResponse.json();
    document.dispatchEvent(
      new CartAddEvent(cart, 'otc-gestures', {
        source: 'otc-gestures',
        itemCount: cart.item_count,
        sections: added.sections,
      })
    );
    return true;
  } catch {
    return false;
  }
}

/**
 * True when the touch is on something that scrolls sideways itself
 * (a product row in the cart drawer, a thumbnail strip), which keeps its own swipe.
 * @param {Element} start
 * @param {Element} stop
 */
function scrollsSideways(start, stop) {
  for (let el = /** @type {Element | null} */ (start); el && el !== stop; el = el.parentElement) {
    if (el.scrollWidth > el.clientWidth + 1) {
      const overflow = getComputedStyle(el).overflowX;
      if (overflow === 'auto' || overflow === 'scroll') return true;
    }
    if (el.matches('slideshow-component, slideshow-slides, scroll-hint')) return true;
  }
  return false;
}

/**
 * True when the touch is inside a list that isn't at its top,
 * so a downward swipe should scroll it rather than close the pop-up.
 * @param {Element} start
 * @param {Element} stop
 */
function scrolledDown(start, stop) {
  for (let el = /** @type {Element | null} */ (start); el; el = el.parentElement) {
    if (el.scrollTop > 0) {
      const overflow = getComputedStyle(el).overflowY;
      if (overflow === 'auto' || overflow === 'scroll') return true;
    }
    if (el === stop) break;
  }
  return false;
}
